(() => {
  // React applications the legacy states open instead of their own views, with
  // the state each one shares with its legacy URL.
  const KPI_TABS = ['constants', 'variables', 'expressions', 'indicators', 'parameters', 'sources'];
  const APPS = {
    'kpi-engine': {
      state: /\.kpiEngine$/,
      fields: { virtualGroupId: () => true, tab: value => KPI_TABS.includes(value) },
    },
    'api-keys': {
      state: /\.dataSource\.apiKey$/,
      fields: { apiKeyId: value => !value.includes('/') },
    },
  };

  // Paths of the former React front whose application has another key in the host.
  const LEGACY_PATHS = {
    'license-plates/search': 'license-plates-search',
    'license-plates/watchlists': 'license-plates-watchlists',
    'license-plates-search/blacklist': 'license-plates-watchlists',
  };

  angular.module('commonServices').factory('NativeAppIntegration', NativeAppIntegration);
  angular.module('commonServices').run(['$transitions', 'EmbeddedHostBridge', 'NativeAppIntegration',
    function ($transitions, bridge, integration) {
      // Embedded in the React host, the legacy app asks it to open the native
      // application instead of nesting it again.
      Object.keys(APPS).forEach(appKey => {
        $transitions.onBefore({ to: state => APPS[appKey].state.test(state.name) }, transition => {
          if (!bridge.isEmbedded()) return;
          bridge.openApplication(appKey, integration.params(appKey, transition.params()));
          return false;
        });
      });
    },
  ]);

  NativeAppIntegration.$inject = ['$window'];
  function NativeAppIntegration($window) {
    let warned = false;
    function baseUrl() {
      try {
        if (!$window.newAppStandaloneURL) return null;
        const url = new URL($window.newAppStandaloneURL, $window.location.origin);
        if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) return null;
        return url.toString().replace(/\/$/, '');
      } catch { return null; }
    }
    function useNative() {
      const enabled = Boolean(baseUrl());
      if (!enabled && !warned) {
        warned = true;
        console.warn('Native apps: newAppStandaloneURL is missing or invalid; using the legacy views.');
      }
      return enabled;
    }
    function validParams(appKey, value) {
      const fields = APPS[appKey] && APPS[appKey].fields;
      return Boolean(fields) && value && typeof value === 'object' && !Array.isArray(value) &&
        Object.entries(value).every(([key, field]) =>
          Object.prototype.hasOwnProperty.call(fields, key) &&
          (field == null || (typeof field === 'string' && field.length <= 256 && fields[key](field))));
    }
    /** Only the application's fields, so a transition's other params never reach the iframe. */
    function params(appKey, value) {
      const result = {};
      Object.keys(APPS[appKey].fields).forEach(key => { result[key] = (value && value[key]) || null; });
      return validParams(appKey, result) ? result : Object.keys(result).reduce((empty, key) => ({ ...empty, [key]: null }), {});
    }
    function key(appKey, value) {
      return Object.keys(APPS[appKey].fields).map(field => value[field] || '').join('|');
    }
    /**
     * Keeps an entry's params and its legacy URL in sync with the iframe. Neither
     * side echoes what the other reported: a late echo can undo a newer navigation.
     */
    function syncState(appKey, vm, $state, toLegacy = value => value) {
      let fromChild = null;
      vm.uiOnParamsChanged = (_params, transition) => {
        const next = params(appKey, transition.params());
        // Cleared on any change: a superseded transition must not hide a later one.
        const echo = fromChild === key(appKey, next);
        fromChild = null;
        if (!echo) vm.params = next;
      };
      vm.onStateChanged = value => {
        if (!validParams(appKey, value)) return;
        const next = toLegacy(params(appKey, value));
        if (key(appKey, next) === key(appKey, params(appKey, $state.params))) return;
        fromChild = key(appKey, next);
        $state.go($state.current.name, next, { notify: false, location: 'replace' });
      };
    }
    /**
     * The host application and canonical route of a project tool, from its
     * path in the former front (`count-report`, `storylines/s1`...).
     */
    function projectRoute(path, customerId, projectId) {
      const [appKey, ...rest] = (LEGACY_PATHS[path] || path).split('/').filter(Boolean);
      return {
        appKey,
        path: `/customers/${encodeURIComponent(customerId)}/projects/${encodeURIComponent(projectId)}/apps/${appKey}` +
          rest.map(segment => `/${encodeURIComponent(segment)}`).join(''),
      };
    }
    /** Banner text inviting to try the React host of this role, or null when it is not configured. */
    function inviteMessage(translate) {
      const url = baseUrl();
      // Host and path: on-prem the host is the legacy one and only /v2 tells them apart.
      return url ? translate.instant('general.newAppInvite', { url, host: url.replace(/^https?:\/\//, '') }) : null;
    }
    return { baseUrl, useNative, validParams, params, syncState, projectRoute, inviteMessage };
  }
})();
